import { axiosInstance } from "@/lib/axios";
import type {
  ConfirmFinanceStepPayload,
  ListingPayload,
  CancelListingPayload,
  FinanceStepResponse,
  OfferPayload,
  OfferItem,
  OffersResponse,
  SingleOfferResponse,
} from "./types";

/**
 * THE SHARED 15s TIMEOUT IS TOO SHORT FOR THESE TWO, and cutting them off is
 * worse than waiting.
 *
 * `axiosInstance` times out at 15s, which is right for a read and wrong for a
 * request that queues chain work: the server keeps going after the client
 * gives up, so an abort here reports a failure for a listing that is on its
 * way. The user then lists again.
 *
 * Raised rather than removed. `0` (wait forever) trades a wrong answer for a
 * dialog that can never end; a minute covers the slow case and still fails
 * eventually if something is genuinely wrong. Either way the chain leg is not
 * what is being waited for here — that is the socket's job, afterwards.
 */
const CHAIN_SUBMIT_TIMEOUT_MS = 60_000;

// ── Listing ──

export async function createListing(
  contractAddress: string,
  tokenId: string | number,
  payload: ListingPayload
): Promise<FinanceStepResponse> {
  const { data } = await axiosInstance.post(
    `/finance/${contractAddress}/${tokenId}/listing`,
    payload,
    { timeout: CHAIN_SUBMIT_TIMEOUT_MS }
  );
  return data;
}

export async function cancelListing(
  contractAddress: string,
  tokenId: string | number,
  payload: CancelListingPayload
): Promise<FinanceStepResponse> {
  const { data } = await axiosInstance.post(
    `/finance/${contractAddress}/${tokenId}/listing/cancel`,
    payload,
    { timeout: CHAIN_SUBMIT_TIMEOUT_MS }
  );
  return data;
}

/**
 * Closes ONE external-wallet listing round — creating a listing and
 * cancelling one share this endpoint, and creating one uses it twice (the
 * NFT approval's hash, then the sale order's signature). Always the
 * `listing_id` the first response returned. Never called for the site's
 * custodial wallet: that path is already terminal when the first POST
 * returns.
 */
export async function confirmListingStep(
  contractAddress: string,
  tokenId: string | number,
  listingId: number | string,
  payload: ConfirmFinanceStepPayload
): Promise<FinanceStepResponse> {
  const { data } = await axiosInstance.post(
    `/finance/${contractAddress}/${tokenId}/listing/${listingId}/confirm`,
    payload
  );
  return data;
}

// ── Offers ──

export type GetOffersParams = {
  limit?: number;
  page?: number;
};

/**
 * The offers on one NFT — V2, and this is the SECOND time this path has been
 * changed. The history matters, because the note that used to live here said
 * the opposite.
 *
 * WHAT THAT NOTE SAID, AND WHY IT WAS RIGHT AT THE TIME. On 2026-08-10 V2 was
 * measured across 15 contract/token pairs and found NOT to be a versioned
 * native-offer list: it answered the on-chain marketplace-bid feed
 * (`tx_hash`/`marketplace`/`price_eth`/`offerer`/`seller`/`block_number`) and
 * carried no `id` and no `status`, so an offer could be created and never
 * accepted. The client was reverted to the non-V2 path for that reason.
 *
 * WHAT IT ANSWERS NOW, measured 2026-09-15 against
 * `0x2267…3251/1`: the SAME four offers as the non-V2 path, with the same
 * `id`s, the same `order_hash`es and the same `status`es. V2 has since become
 * the real offers list — and it is the one to use, because it applies the
 * visibility rule the product wants: everyone sees `submitted`/`accepted`,
 * while the offer's own author sees ALL of theirs whatever the status. The
 * non-V2 path cannot show an author their own `pending*` offers.
 *
 * ITS PROJECTION IS DIFFERENT, AND THAT IS WHAT `normaliseV2Offer` IS FOR.
 * V2 names the price `price` and the time `timestamp`, and it does NOT send
 * `user_id`, `quantity`, `deadline`, `decimals` or `currency_token_address`.
 * The first of those is the one with teeth: cancel eligibility keyed on
 * `user_id`, so without a replacement the author of an offer would lose their
 * own Cancel button. `offerer` (their address) is carried through in its place
 * — see `canCancelOffer`.
 *
 * The absent `quantity` and `deadline` are left ABSENT rather than defaulted:
 * the accept/cancel review omits a row it has no value for, which is honest,
 * where a fabricated `1` or a fabricated expiry would be read as fact on a
 * screen that moves money.
 */
type V2OfferRow = {
  id?: number | string | null;
  status?: string | null;
  order_hash?: string | null;
  price?: number | string | null;
  offerer?: string | null;
  timestamp?: string | null;
  token_id?: string | number | null;
  /** Present on the non-V2 shape; kept so a mixed/rolled-back response still works. */
  total_price?: number | string | null;
  user_id?: number | null;
  quantity?: number | string | null;
  deadline?: number | string | null;
  created_at?: string | null;
  offerer_address?: string | null;
};

/**
 * V2 row → the `OfferItem` the UI has always consumed.
 *
 * TOLERANT OF BOTH SHAPES ON PURPOSE. Each field falls back to its non-V2
 * spelling, so a rolled-back backend — or a response that mixes the two —
 * keeps working instead of rendering a list of blanks. Given how this
 * endpoint has already changed twice, that is not hypothetical caution.
 */
function normaliseV2Offer(row: V2OfferRow): OfferItem {
  return {
    ...(row as object),
    id: Number(row.id),
    status: (row.status ?? "") as OfferItem["status"],
    total_price: (row.total_price ?? row.price ?? 0) as OfferItem["total_price"],
    created_at: row.created_at ?? row.timestamp ?? undefined,
    // The address the offer was made from. V2's only identity for its author,
    // and what stands in for `user_id` when deciding whose offer this is.
    offerer_address: row.offerer_address ?? row.offerer ?? null,
  } as OfferItem;
}

export async function getOffers(
  contractAddress: string,
  tokenId: string | number,
  params?: GetOffersParams
): Promise<OffersResponse> {
  const { data } = await axiosInstance.get(
    `/finance/V2/${contractAddress}/${tokenId}/offer`,
    {
      params: {
        limit: params?.limit,
        page: params?.page,
      },
    }
  );

  const rawItems = data?.data?.items ?? data?.data?.offers ?? data?.data ?? [];

  return {
    data: {
      items: Array.isArray(rawItems) ? rawItems.map(normaliseV2Offer) : [],
      page: data?.data?.page ?? data?.meta ?? undefined,
    },
    message: data?.message ?? null,
    next: data?.next ?? null,
    errors: data?.errors ?? null,
    meta: data?.meta ?? [],
  };
}

export async function placeOffer(
  contractAddress: string,
  tokenId: string | number,
  payload: OfferPayload
): Promise<SingleOfferResponse> {
  const { data } = await axiosInstance.post(
    `/finance/${contractAddress}/${tokenId}/offer`,
    payload,
    { timeout: CHAIN_SUBMIT_TIMEOUT_MS }
  );
  return data;
}

export async function cancelOffer(
  contractAddress: string,
  tokenId: string | number,
  offerId: number | string
): Promise<SingleOfferResponse> {
  const { data } = await axiosInstance.post(
    `/finance/${contractAddress}/${tokenId}/offer/${offerId}/cancel`,
    // Still bodyless — `undefined`, not `{}`: the contract is no body, and
    // an empty object is a body.
    undefined,
    { timeout: CHAIN_SUBMIT_TIMEOUT_MS }
  );
  return data;
}

export async function acceptOffer(
  contractAddress: string,
  tokenId: string | number,
  offerId: number | string
): Promise<SingleOfferResponse> {
  const { data } = await axiosInstance.post(
    `/finance/${contractAddress}/${tokenId}/offer/${offerId}/accept`,
    // Still bodyless — `undefined`, not `{}`: the contract is no body, and
    // an empty object is a body.
    undefined,
    { timeout: CHAIN_SUBMIT_TIMEOUT_MS }
  );
  return data;
}

/**
 * Closes ONE external-wallet offer round — PLACING an offer and CANCELLING
 * one share this endpoint. Placing uses it up to three times (the wrap's
 * hash, the approval's hash, then the bid's signature); cancelling once.
 *
 * Note the URL: only ACCEPTING confirms on `/accept/confirm`
 * (`confirmAcceptOfferStep` below). Posting an accept round here — the easy
 * mistake, since the offer id is the same — is a different endpoint and does
 * not advance the accept sequence.
 */
export async function confirmOfferStep(
  contractAddress: string,
  tokenId: string | number,
  offerId: number | string,
  payload: ConfirmFinanceStepPayload
): Promise<FinanceStepResponse> {
  const { data } = await axiosInstance.post(
    `/finance/${contractAddress}/${tokenId}/offer/${offerId}/confirm`,
    payload
  );
  return data;
}

/** Closes ONE round of the accept sequence: the NFT approval, then the Seaport settlement. */
export async function confirmAcceptOfferStep(
  contractAddress: string,
  tokenId: string | number,
  offerId: number | string,
  payload: ConfirmFinanceStepPayload
): Promise<FinanceStepResponse> {
  const { data } = await axiosInstance.post(
    `/finance/${contractAddress}/${tokenId}/offer/${offerId}/accept/confirm`,
    payload
  );
  return data;
}
